import type { UserSchema } from '@libs/data-access/entities'
import type { AccountSchema } from '@libs/data-access/queries'
import {
  accountService,
  userService,
  wsServices
} from '@libs/data-access/queries'
import { Loader, SetDesignSystemConfig } from '@libs/ui/ds'
import { errorMonitoringService } from '@libs/utils/error-monitoring'
import i18n, { datesService } from '@libs/utils/translations'
import { useInitTitle } from '@spa/core/browser'
import { useInitGTM } from '@spa/core/externals/gtm'
import { useInitMaze } from '@spa/core/externals/maze'
import { trackerService } from '@spa/core/externals/segment'
import { UpscopeScript } from '@spa/core/externals/upscope/UpscopeScript'
import { fetchAddons } from '@spa/redux/billing/addons/modules/addons'
import { fetchLocationBilling } from '@spa/redux/billing/locations/modules/locationBilling'
import { fetchBillingPlans } from '@spa/redux/billing/plan/modules/plan'
import { useMemoSelector } from '@spa/redux/hooks'
import { useAuthState, useClearAuth } from '@spa/scenes/Auth/shared/services'
import { isNumber } from 'lodash-es'
import type { FC } from 'react'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { fetchAccount } from 'snap-redux/modules/accounts'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { fetchCountryConfigurations } from 'snap-redux/modules/countryConfigurations'
import { fetchLocations } from 'snap-redux/modules/locations'
import { openSocketConnection } from 'snap-redux/modules/socket'
import { fetchUserSucceeded } from 'snap-redux/modules/users'
import { useActions } from 'snap-redux/utils'

import { AuthenticatedAppRoutes } from './AuthenticatedApp.routes'
import ExpiredTrialRoutes from './ExpiredTrial.routes'
import { useCanAccessApp } from './authorization/useCanAccessApp'
import { Banners, TimezoneInit } from './components'
import { LegacyAppNavBar } from './components/MainNavbar/LegacyNavbar/LegacyAppNavBar'
import { MainNavbar } from './components/MainNavbar/MainNavbar'
import { useSetAccountStatusCookie } from './utils/useSetAccountStatusCookie'

// Async action creators
const actionCreators = {
  fetchAccount,
  fetchLocations,
  fetchAddons,
  fetchBillingPlans
}

export const AuthenticatedApp: FC = () => {
  const dispatch = useDispatch()
  const authState = useAuthState()
  const clearAuth = useClearAuth()
  const actions = useActions(actionCreators)

  const account = useMemoSelector(getCurrentAccount)
  const accessAppQuery = useCanAccessApp()

  trackerService.useInitSegment()
  trackerService.useSegment()
  useInitGTM()
  useInitTitle()
  useSetAccountStatusCookie()
  useInitMaze()

  const accountFlipperFeatureFlags =
    accountService.featureFlags.useFetchAllForAccount({
      enabled: false
    })

  const coreWSServiceConnect = wsServices.core.ws.useConnect({
    onSuccess: () => {
      dispatch(openSocketConnection())
    }
  })

  const userMeQuery = userService.user.useFetchCurrentUser(
    {},
    { enabled: false }
  )

  const setupConfigurations = (
    userData: UserSchema | undefined,
    account: AccountSchema
  ) => {
    if (userData) {
      const locale = userData.locale.toLowerCase()
      i18n.changeLanguage(locale)
      datesService.setLocale(locale)
      errorMonitoringService.identify(userMeQuery.data, account)
    }
  }

  const fetchCoreData = async () => {
    Promise.all([
      accountFlipperFeatureFlags.refetch(),
      userMeQuery.refetch(),
      actions.fetchAccount(authState.auth.accountId, {}),
      actions.fetchLocations(authState.auth.accountId)
    ])
      .then(([_, user, account]) => {
        dispatch(fetchUserSucceeded(user.data))
        setupConfigurations(user.data, account)
      })
      .catch(() => {
        clearAuth()
      })
  }

  // componentDidMount
  useEffect(() => {
    const account_id = authState.auth.accountId as number
    fetchCoreData()

    dispatch(
      fetchCountryConfigurations({
        account_id
      })
    )
    dispatch(fetchLocationBilling({ account_id }))

    if (isNumber(account_id)) {
      actions.fetchAddons()
      actions.fetchBillingPlans()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.auth.accountId])

  if (
    !account ||
    !userMeQuery.isSuccess ||
    coreWSServiceConnect.isPending ||
    accountFlipperFeatureFlags.isLoading
  ) {
    return <Loader.Overlay />
  }

  return (
    <div className='h-full sm:w-full md:min-w-screen-md'>
      <SetDesignSystemConfig
        config={{
          timezone: account?.time_zone,
          language: userMeQuery.data.locale.toLowerCase()
        }}
      />
      <UpscopeScript />
      <TimezoneInit account={account} />
      <Banners isBillingError={accessAppQuery.isError} />

      {accountFlipperFeatureFlags?.data
        ?.frontend_foundation_enable_new_main_navbar ? (
        <MainNavbar />
      ) : (
        <LegacyAppNavBar account={account} />
      )}

      {accessAppQuery.canAccessApp ? (
        <AuthenticatedAppRoutes />
      ) : (
        <ExpiredTrialRoutes />
      )}
    </div>
  )
}
